import { App as AppWrapper, ConfigProvider } from "antd";
import store from "./store";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { Provider } from "react-redux";
import { ErrorBoundary } from "react-error-boundary";
import { Suspense, lazy, useEffect } from "react";
import "react-toastify/dist/ReactToastify.css";
import "./helpers/i18n.ts";

import { ErrorFallbackPage } from "./helpers/ErrorFallbackPage";
import { SiteLoader } from "./Components/Components";
import ToastHandler from "./helpers/ToastHandler";
import { alias } from "./helpers/utils";
import { sideMenuBarData } from "./Assets/navigation";
import Home from "./Components/Pages/Home";
import DeletedConsumables from "./Admin/Pages/MasterData/DeletedConsumables";
import { loadChunkWithRetry, withLazy } from "./helpers/LazyWrapper";
import { Bounce, ToastContainer } from "react-toastify";
import { Loader } from "@googlemaps/js-api-loader";
import { decryptByText } from "./helpers/bycrypt";
import { BoatProvider } from "./helpers/providers/BoatProvider";

const Boats = withLazy(lazy(() => loadChunkWithRetry(() => import("./Admin/Pages/Boats/Boats"))));
const BoatForm = withLazy(lazy(() => loadChunkWithRetry(() => import("./Admin/Pages/Boats/BoatForm"))));
const BoatDetails = withLazy(lazy(() => loadChunkWithRetry(() => import("./Admin/Pages/Boats/BoatDetails"))));
const BoatReports = withLazy(lazy(() => loadChunkWithRetry(() => import("./Admin/Pages/Boats/BoatReports"))));
const BoatChecklists = withLazy(lazy(() => loadChunkWithRetry(() => import("./Admin/Pages/Boats/BoatChecklists"))));
const ManageBoatChecklists = withLazy(
  lazy(() => loadChunkWithRetry(() => import("./Admin/Pages/Boats/ManageBoatChecklists")))
);
const BoatHaulPlan = withLazy(lazy(() => loadChunkWithRetry(() => import("./Admin/Pages/Boats/BoatHaulPlan"))));
const BoatStoragePlan = withLazy(lazy(() => loadChunkWithRetry(() => import("./Admin/Pages/Boats/BoatStoragePlan"))));
const BoatMastsAndDinghies = withLazy(
  lazy(() => loadChunkWithRetry(() => import("./Admin/Pages/Boats/BoatMastsAndDinghies")))
);
const BoatItems = withLazy(lazy(() => loadChunkWithRetry(() => import("./Admin/Pages/Boats/BoatItems"))));
const BoatStorageContracts = withLazy(
  lazy(() => loadChunkWithRetry(() => import("./Admin/Pages/Boats/BoatStorageContracts")))
);
const ChangeBoatContract = withLazy(
  lazy(() => loadChunkWithRetry(() => import("./Customer/Pages/BoatStorageContract/ChangeBoatContract")))
);
const YardMap = withLazy(lazy(() => loadChunkWithRetry(() => import("./Admin/Pages/Yard/YardMap"))));
const AssetsBodyComponent = withLazy(
  lazy(() => loadChunkWithRetry(() => import("./Admin/Components/AssetsBodyComponent")))
);
const ManageLocations = withLazy(lazy(() => loadChunkWithRetry(() => import("./Admin/Pages/Yard/ManageLocations"))));
const EmployeeLocations = withLazy(
  lazy(() => loadChunkWithRetry(() => import("./Admin/Pages/Yard/EmployeeLocations")))
);
const StoredBoatQRPage = withLazy(lazy(() => loadChunkWithRetry(() => import("./Admin/Pages/Yard/StoredBoatQRPage"))));

const Checklists = withLazy(lazy(() => loadChunkWithRetry(() => import("./Admin/Pages/Checklists/Checklists"))));
const ChecklistDetails = withLazy(
  lazy(() => loadChunkWithRetry(() => import("./Admin/Pages/Checklists/ChecklistDetails")))
);
const CreateChecklist = withLazy(
  lazy(() => loadChunkWithRetry(() => import("./Admin/Pages/Checklists/CreateChecklist")))
);
const FillChecklistResponse = withLazy(
  lazy(() => loadChunkWithRetry(() => import("./Admin/Pages/Checklists/FillChecklistResponse")))
);
const ChecklistResponses = withLazy(
  lazy(() => loadChunkWithRetry(() => import("./Admin/Pages/Checklists/ChecklistResponses")))
);
const ViewChecklistResponse = withLazy(
  lazy(() => loadChunkWithRetry(() => import("./Admin/Pages/Checklists/ViewChecklistResponse")))
);

const CustomerInquiry = withLazy(lazy(() => loadChunkWithRetry(() => import("./Admin/Pages/Leads/CustomerInquiry"))));
const Login = withLazy(lazy(() => loadChunkWithRetry(() => import("./Auth/Pages/Login"))));

const Profile = withLazy(lazy(() => loadChunkWithRetry(() => import("./Admin/Pages/Profile/Profile"))));
const Dashboard = withLazy(lazy(() => loadChunkWithRetry(() => import("./Admin/Pages/Dashboard/Dashboard"))));

const Estimates = withLazy(lazy(() => loadChunkWithRetry(() => import("./Admin/Pages/Estimates/Estimates"))));
const CreateEstimateWithoutLead = withLazy(
  lazy(() => loadChunkWithRetry(() => import("./Admin/Pages/Estimates/CreateEstimateWithoutLead")))
);
const CreateEstimate = withLazy(lazy(() => loadChunkWithRetry(() => import("./Admin/Pages/Estimates/CreateEstimate"))));
const EstimateDetails = withLazy(
  lazy(() => loadChunkWithRetry(() => import("./Admin/Pages/Estimates/EstimateDetails")))
);
const CreateChangeOrder = withLazy(
  lazy(() => loadChunkWithRetry(() => import("./Admin/Pages/Estimates/CreateChangeOrder")))
);
const ChangeOrderDetails = withLazy(
  lazy(() => loadChunkWithRetry(() => import("./Admin/Pages/Estimates/ChangeOrderDetails")))
);
const ApproveEstimate = withLazy(
  lazy(() => loadChunkWithRetry(() => import("./Customer/Pages/Estimate/ApproveEstimate")))
);
const ApproveChangeOrder = withLazy(
  lazy(() => loadChunkWithRetry(() => import("./Customer/Pages/Estimate/ApproveChangeOrder")))
);
const PayInvoiceByClient = withLazy(
  lazy(() => loadChunkWithRetry(() => import("./Customer/Pages/Inovoices/PayInvoiceByClient")))
);
const SecondHalfStoragePay = withLazy(
  lazy(() => loadChunkWithRetry(() => import("./Customer/Pages/Inovoices/SecondHalfStoragePay")))
);
const TimeSheets = withLazy(lazy(() => loadChunkWithRetry(() => import("./Admin/Pages/TimeSheets/TimeSheets"))));
const ViewTimeSheetDetails = withLazy(
  lazy(() => loadChunkWithRetry(() => import("./Admin/Pages/TimeSheets/ViewTimeSheetDetails")))
);
const ClockInOutHistory = withLazy(
  lazy(() => loadChunkWithRetry(() => import("./Admin/Pages/TimeSheets/ClockInOutHistory")))
);
const Leads = withLazy(lazy(() => loadChunkWithRetry(() => import("./Admin/Pages/Leads/Leads"))));

const CreateLead = withLazy(lazy(() => loadChunkWithRetry(() => import("./Admin/Pages/Leads/CreateLead"))));
const ViewLeadDetails = withLazy(lazy(() => loadChunkWithRetry(() => import("./Admin/Pages/Leads/ViewLeadDetails"))));
const BoatStorageContract = withLazy(
  lazy(() => loadChunkWithRetry(() => import("./Customer/Pages/BoatStorageContract/BoatStorageContract")))
);

const ConditionReports = withLazy(
  lazy(() => loadChunkWithRetry(() => import("./Admin/Pages/ConditionReports/ConditionReports")))
);
const ReportDetails = withLazy(
  lazy(() => loadChunkWithRetry(() => import("./Admin/Pages/ConditionReports/ReportDetails")))
);
const ConditionReportForm = withLazy(
  lazy(() => loadChunkWithRetry(() => import("./Admin/Pages/ConditionReports/ConditionReportForm")))
);

const Customer = withLazy(lazy(() => loadChunkWithRetry(() => import("./Admin/Pages/Customer/Customer"))));
const ViewCustomerDetails = withLazy(
  lazy(() => loadChunkWithRetry(() => import("./Admin/Pages/Customer/ViewCustomerDetails")))
);

const Issues = withLazy(lazy(() => loadChunkWithRetry(() => import("./Admin/Pages/Issues/Issues"))));
const WorkOrder = withLazy(lazy(() => loadChunkWithRetry(() => import("./Admin/Pages/WorkOrders/WorkOrders"))));
const CreateWorkOrder = withLazy(
  lazy(() => loadChunkWithRetry(() => import("./Admin/Pages/WorkOrders/CreateWorkOrders")))
);
const WorkOrderDetails = withLazy(
  lazy(() => loadChunkWithRetry(() => import("./Admin/Pages/WorkOrders/WorkOrderDetails")))
);
const Invoices = withLazy(lazy(() => loadChunkWithRetry(() => import("./Admin/Pages/Invoices/Invoices"))));
const CreateInvoice = withLazy(lazy(() => loadChunkWithRetry(() => import("./Admin/Pages/Invoices/CreateInvoice"))));
const InvoicesDetails = withLazy(lazy(() => loadChunkWithRetry(() => import("./Admin/Pages/Invoices/InvoiceDetails"))));
const ApprovalInvoiceList = withLazy(
  lazy(() => loadChunkWithRetry(() => import("./Admin/Pages/Invoices/ApprovalInvoiceList")))
);
const ApprovalInvoiceDetails = withLazy(
  lazy(() => loadChunkWithRetry(() => import("./Admin/Pages/Invoices/ApprovalInvoiceDetails")))
);

const PublicWorkorderDetails = withLazy(
  lazy(() => loadChunkWithRetry(() => import("./PublicPages/Pages/PublicWorkorderDetails")))
);
const PublicEstimateDetails = withLazy(
  lazy(() => loadChunkWithRetry(() => import("./PublicPages/Pages/PublicEstimateDetails")))
);
const PublicEmployeeDetails = withLazy(
  lazy(() => loadChunkWithRetry(() => import("./PublicPages/Pages/PublicEmployeeDetails")))
);

const Task = withLazy(lazy(() => loadChunkWithRetry(() => import("./Admin/Pages/Task/Task"))));
const TaskDeatils = withLazy(lazy(() => loadChunkWithRetry(() => import("./Admin/Pages/Task/TaskDeatils"))));

const Contacts = withLazy(lazy(() => loadChunkWithRetry(() => import("./Admin/Pages/Contacts/Contacts"))));
const CreateContact = withLazy(lazy(() => loadChunkWithRetry(() => import("./Admin/Pages/Contacts/CreateContact"))));
const ViewContactDetails = withLazy(
  lazy(() => loadChunkWithRetry(() => import("./Admin/Pages/Contacts/ViewContactDetails")))
);

const HrModule = withLazy(lazy(() => loadChunkWithRetry(() => import("./Admin/Pages/HrModule/HrModule"))));

const Inventory = withLazy(lazy(() => loadChunkWithRetry(() => import("./Admin/Pages/Inventory/Inventory"))));
const CreateNewSKU = withLazy(lazy(() => loadChunkWithRetry(() => import("./Admin/Pages/Inventory/CreateNewSKU"))));
const ViewInventory = withLazy(lazy(() => loadChunkWithRetry(() => import("./Admin/Pages/Inventory/ViewInventory"))));

const Schedules = withLazy(lazy(() => loadChunkWithRetry(() => import("./Admin/Pages/Schedules/Schedules"))));

const InventorySignIn = withLazy(
  lazy(() => loadChunkWithRetry(() => import("./Admin/Pages/Inventory/InventorySignIn")))
);
const InventorySignInHistory = withLazy(
  lazy(() => loadChunkWithRetry(() => import("./Admin/Pages/Inventory/InventorySignInHistory")))
);

const InventorySignOut = withLazy(
  lazy(() => loadChunkWithRetry(() => import("./Admin/Pages/Inventory/InventorySignOut")))
);
const InventorySignOutHistory = withLazy(
  lazy(() => loadChunkWithRetry(() => import("./Admin/Pages/Inventory/InventorySignOutHistory")))
);
const CreateEmployee = withLazy(lazy(() => loadChunkWithRetry(() => import("./Admin/Pages/HrModule/CreateEmployee"))));
const DeletedEmployee = withLazy(
  lazy(() => loadChunkWithRetry(() => import("./Admin/Pages/HrModule/DeletedEmployee")))
);

const ViewEmployeeDetail = withLazy(
  lazy(() => loadChunkWithRetry(() => import("./Admin/Pages/HrModule/ViewEmployeeDetail")))
);

const PtoHistory = withLazy(
  lazy(() => loadChunkWithRetry(() => import("./Admin/Pages/HrModule/PtoHistory")))
);

const Assets = withLazy(lazy(() => loadChunkWithRetry(() => import("./Admin/Pages/MasterData/Assets"))));
const Categories = withLazy(lazy(() => loadChunkWithRetry(() => import("./Admin/Pages/MasterData/Categories"))));
const DeletedCategories = withLazy(
  lazy(() => loadChunkWithRetry(() => import("./Admin/Pages/MasterData/DeletedCategories")))
);
const Services = withLazy(lazy(() => loadChunkWithRetry(() => import("./Admin/Pages/MasterData/Services"))));
const DeletedServices = withLazy(
  lazy(() => loadChunkWithRetry(() => import("./Admin/Pages/MasterData/DeletedServices")))
);
const ViewServiceDetails = withLazy(
  lazy(() => loadChunkWithRetry(() => import("./Admin/Pages/MasterData/ViewServiceDetails")))
);

const Tasks = withLazy(lazy(() => loadChunkWithRetry(() => import("./Admin/Pages/MasterData/Tasks"))));
const DeletedTasks = withLazy(lazy(() => loadChunkWithRetry(() => import("./Admin/Pages/MasterData/DeletedTasks"))));
const Materials = withLazy(lazy(() => loadChunkWithRetry(() => import("./Admin/Pages/MasterData/Materials"))));
const DeletedMaterials = withLazy(
  lazy(() => loadChunkWithRetry(() => import("./Admin/Pages/MasterData/DeletedMaterials")))
);
const Consumables = withLazy(lazy(() => loadChunkWithRetry(() => import("./Admin/Pages/MasterData/Consumables"))));
const DeletedAssets = withLazy(lazy(() => loadChunkWithRetry(() => import("./Admin/Pages/MasterData/DeletedAssets"))));

const AdminSettings = withLazy(
  lazy(() => loadChunkWithRetry(() => import("./Admin/Pages/AdminSettings/AdminSettings")))
);
const EditStaticPage = withLazy(
  lazy(() => loadChunkWithRetry(() => import("./Admin/Pages/AdminSettings/EditStaticPage")))
);
const ViewStaticPage = withLazy(
  lazy(() => loadChunkWithRetry(() => import("./Admin/Pages/AdminSettings/ViewStaticPage")))
);
const AddEditRoleAndPermission = withLazy(
  lazy(() => loadChunkWithRetry(() => import("./Admin/Pages/AdminSettings/AddEditRoleAndPermission")))
);
const EditDetailsContactDetails = withLazy(
  lazy(() => loadChunkWithRetry(() => import("./Admin/Pages/AdminSettings/EditDetailsContactDetails")))
);
const StaticData = withLazy(lazy(() => loadChunkWithRetry(() => import("./Admin/Pages/AdminSettings/StaticData"))));
const EmailTemplate = withLazy(
  lazy(() => loadChunkWithRetry(() => import("./Admin/Pages/AdminSettings/EmailTemplates")))
);
const EditEmailTemplate = withLazy(
  lazy(() => loadChunkWithRetry(() => import("./Admin/Pages/AdminSettings/EditEmailTemplate")))
);
const StaticPages = withLazy(lazy(() => loadChunkWithRetry(() => import("./Admin/Pages/AdminSettings/StaticPages"))));
const QuickBooks = withLazy(lazy(() => loadChunkWithRetry(() => import("./Admin/Pages/QuickBooks/QuickBooks"))));
const Xero = withLazy(lazy(() => loadChunkWithRetry(() => import("./Admin/Pages/Xero/Xero"))));

const ChangePassword = withLazy(lazy(() => loadChunkWithRetry(() => import("./Admin/Pages/ChangePassword"))));
const ForgotPassword = withLazy(lazy(() => loadChunkWithRetry(() => import("./Auth/Pages/ForgotPassword"))));
const ResetPassword = withLazy(lazy(() => loadChunkWithRetry(() => import("./Auth/Pages/Resetpassword"))));

const PageNotFound = withLazy(lazy(() => loadChunkWithRetry(() => import("./Components/Error"))));
const AdminIndex = withLazy(lazy(() => loadChunkWithRetry(() => import("./Admin/AdminIndex"))));

const InvoiceDownload = withLazy(
  lazy(() => loadChunkWithRetry(() => import("./Admin/Pages/PDFDownloadPages/InvoiceDownload")))
);
const StorageContractDownload = withLazy(
  lazy(() => loadChunkWithRetry(() => import("./Admin/Pages/PDFDownloadPages/StorageContractDownload")))
);
// Need To remove after finish project
const CryptoFunction = withLazy(lazy(() => loadChunkWithRetry(() => import("./Admin/Pages/CryptoFunction"))));

function App() {
  useEffect(() => {
    const key = decryptByText(process.env.REACT_APP_GOOGLE_MAP_KEY);
    const loader = new Loader({
      apiKey: key,
      libraries: ["places"],
    });

    const importMaps = loader.importLibrary("maps");
    const importPlaces = loader.importLibrary("places");

    Promise.all([importMaps, importPlaces]).then(
      () => console.info("Google Maps loaded with status ", loader.status),
      () => console.warn("Google Maps failed to load")
    );
  }, []);
  return (
    <ConfigProvider
      theme={{
        token: {
          fontFamily: "Nunito Sans Regular",
        },
      }}
    >
      <ToastContainer
        position="top-center"
        autoClose={10000}
        limit={1}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick={false}
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
        transition={Bounce}
      />
      <AppWrapper>
        <Provider store={store}>
          <BrowserRouter basename={alias} future={{ v7_relativeSplatPath: true }}>
            {/* <BrowserRouter future={{ v7_relativeSplatPath: true }}>  */}
            <Suspense fallback={<SiteLoader />}>
              <ErrorBoundary FallbackComponent={ErrorFallbackPage}>
                <Routes>
                  {/* need to remove after finish prject  */}
                  {/* <Route path="/decode" element={<CryptoFunction />} /> */}
                  <Route path="stored-boat-details/:id" element={<StoredBoatQRPage />} />
                  <Route path="/auth">
                    <Route path="login" element={<Login />} />
                    <Route path="forgot-password" element={<ForgotPassword />} />
                    <Route path="*" element={<PageNotFound />} />
                    <Route path="forgot-password/:token" element={<ResetPassword />} />
                  </Route>
                  <Route path="/download-storage-contract-invoice/:id" element={<InvoiceDownload />} />
                  <Route path="/download-storage-contract/:id" element={<StorageContractDownload />} />
                  <Route path="approve-estimate/:id" element={<ApproveEstimate />} />
                  <Route path="approve-change-order/:id" element={<ApproveChangeOrder />} />
                  <Route path="pay-invoice/:id" element={<PayInvoiceByClient />} />
                  <Route path="pay-second-half-storage-invoice/:id" element={<SecondHalfStoragePay />} />

                  <Route path="storage-contract/:id" element={<BoatStorageContract />} />
                  <Route path="change-storage-contract/:id" element={<ChangeBoatContract />} />

                  <Route path="inquiry/:business" element={<CustomerInquiry />} />
                  <Route path="inquiry" element={<CustomerInquiry />} />
                  <Route path="work-order-details-view/:workorderId" element={<PublicWorkorderDetails />} />
                  <Route path="estimate-details-view/:id" element={<PublicEstimateDetails />} />
                  <Route path="employee-details/:id" element={<PublicEmployeeDetails />} />

                  <Route path="static-page/view/:businessId/:url" element={<ViewStaticPage />} />

                  <Route path="/">
                    <Route
                      path="dashboard"
                      element={
                        <AdminIndex moduleName="dashboard">
                          <Dashboard />
                        </AdminIndex>
                      }
                    />
                    <Route
                      path="change-password"
                      element={
                        <AdminIndex moduleName="change-password" notRequired={true}>
                          <ChangePassword />
                        </AdminIndex>
                      }
                    />
                    <Route
                      path="profile"
                      element={
                        <AdminIndex moduleName="profile" notRequired={true}>
                          <Profile />
                        </AdminIndex>
                      }
                    />

                    <Route path="leads" element={<AdminIndex moduleName="Leads" />}>
                      <Route path="" element={<Leads />} />
                      <Route path="create-lead" element={<CreateLead />} />
                      <Route path="edit-lead/:id" element={<CreateLead />} />
                      <Route path="lead-details/:id" element={<ViewLeadDetails />} />
                    </Route>
                    <Route path="estimates" element={<AdminIndex moduleName="Estimate" />}>
                      <Route path="" element={<Estimates />} />
                      <Route path="create-estimate-without-lead/:customerId" element={<CreateEstimateWithoutLead />} />
                      <Route
                        path="edit-estimate-without-lead/:customerId/:estimateId"
                        element={<CreateEstimateWithoutLead />}
                      />
                      <Route path="create-estimate/:id" element={<CreateEstimate />} />
                      <Route path="estimate-details/:id" element={<EstimateDetails />} />
                      <Route path="change-order-details/:id" element={<ChangeOrderDetails />} />
                      <Route path="edit-estimate/:id/:estimateId" element={<CreateEstimate />} />
                      <Route path="create-change-order/:id" element={<CreateChangeOrder />} />
                      <Route path="edit-change-order/:id" element={<CreateChangeOrder />} />
                    </Route>

                    <Route path="work-orders" element={<AdminIndex moduleName="Work" />}>
                      <Route path="" element={<WorkOrder />} />
                      <Route path="create-work-order/:estimateId" element={<CreateWorkOrder />} />
                      <Route path="edit-work-order/:estimateId/:workorderId" element={<CreateWorkOrder />} />
                      <Route path="work-order-details/:workorderId" element={<WorkOrderDetails />} />
                    </Route>
                    <Route path="invoices" element={<AdminIndex moduleName="Invoices" />}>
                      <Route path="" element={<Invoices />} />
                      <Route path="approval" element={<ApprovalInvoiceList />} />
                      <Route path="approval/:invoiceId" element={<ApprovalInvoiceDetails />} />
                      <Route path="create-invoice" element={<CreateInvoice />} />
                      <Route path="invoice-details/:invoiceId" element={<InvoicesDetails />} />
                      <Route path="edit-invoice/:invoiceId" element={<CreateInvoice />} />
                    </Route>
                    <Route path="schedules" element={<AdminIndex moduleName="Schedules" />}>
                      <Route path="" element={<Schedules />} />
                    </Route>
                    <Route path="tasks" element={<AdminIndex moduleName="Task" />}>
                      <Route path="" element={<Task />} />
                      <Route path="task-details/:taskId" element={<TaskDeatils />} />
                    </Route>
                    <Route path="checklists" element={<AdminIndex moduleName="Checklists" />}>
                      <Route index element={<Checklists />} />
                      <Route path="create-checklist" element={<CreateChecklist />} />
                      <Route path="edit-checklist/:id" element={<CreateChecklist />} />
                      <Route path="duplicate-checklist/:id" element={<CreateChecklist />} />
                      <Route path="checklist-details/:id" element={<ChecklistDetails />} />
                      <Route path="checklist-responses/:id" element={<ChecklistResponses />} />
                      <Route path="fill-response" element={<FillChecklistResponse />} />
                      <Route path="view-response/:id" element={<ViewChecklistResponse />} />
                    </Route>
                    <Route path="time-sheets" element={<AdminIndex moduleName="Timesheets" />}>
                      <Route path="timesheets" element={<TimeSheets />} />
                      <Route path="timesheets/details/:id" element={<ViewTimeSheetDetails />} />
                      <Route path="clock-history" element={<ClockInOutHistory />} />
                    </Route>
                    <Route path="customers" element={<AdminIndex moduleName="Contacts" />}>
                      <Route path="" element={<Customer />} />
                      <Route path="customer-details/:id" element={<ViewCustomerDetails />} />
                    </Route>
                    <Route
                      path="issues"
                      element={
                        <AdminIndex moduleName="Issues">
                          <Issues />
                        </AdminIndex>
                      }
                    />

                    <Route path="yard" element={<AdminIndex moduleName="Yard" />}>
                      <Route path="" element={<YardMap />} />
                      <Route path="manage-locations" element={<ManageLocations />} />
                      <Route path="assets" element={<AssetsBodyComponent />} />
                      <Route path="employee-locations" element={<EmployeeLocations />} />
                    </Route>

                    <Route
                      path="boats"
                      element={
                        <BoatProvider>
                          <AdminIndex moduleName="Boat" />
                        </BoatProvider>
                      }
                    >
                      <Route path="" element={<Boats />} />
                      <Route path="add-boat" element={<BoatForm />} />
                      <Route path="edit-boat/:boatId" element={<BoatForm />} />
                      <Route path=":boatId" element={<BoatDetails />} />
                      <Route path=":boatId/reports" element={<BoatReports />} />
                      <Route path=":boatId/checklists" element={<BoatChecklists />} />
                      <Route path=":boatId/manage-checklists" element={<ManageBoatChecklists />} />
                      <Route path=":boatId/haul-plan" element={<BoatHaulPlan />} />
                      <Route path=":boatId/storage-plan" element={<BoatStoragePlan />} />
                      <Route path=":boatId/masts-and-dinghies" element={<BoatMastsAndDinghies />} />
                      <Route path=":boatId/items" element={<BoatItems />} />
                      <Route path=":boatId/storage-contract" element={<BoatStorageContracts />} />
                    </Route>

                    <Route path="condition-reports" element={<AdminIndex moduleName="Condition Reports" />}>
                      <Route path="" element={<ConditionReports />} />
                      <Route path="report-details/:id" element={<ReportDetails />} />
                      <Route path="add-report" element={<ConditionReportForm />} />
                      <Route path="edit-report/:id" element={<ConditionReportForm />} />
                    </Route>

                    <Route path="contacts" element={<AdminIndex moduleName="Contacts" />}>
                      <Route path="" element={<Contacts />} />
                      <Route path="contact-details/:id" element={<ViewContactDetails />} />
                      <Route path="create-contact" element={<CreateContact />} />
                      <Route path="edit-contact/:id" element={<CreateContact />} />
                    </Route>
                    <Route path="inventory" element={<AdminIndex moduleName="Inventory" />}>
                      <Route path="" element={<Inventory />} />
                      <Route path="create-sku" element={<CreateNewSKU />} />
                      <Route path="edit-sku/:id" element={<CreateNewSKU />} />
                      <Route path="sku-details/:id" element={<ViewInventory />} />
                      <Route path="sign-in" element={<InventorySignIn />} />
                      <Route path="sign-in-history" element={<InventorySignInHistory />} />
                      <Route path="sign-out" element={<InventorySignOut />} />
                      <Route path="sign-out-history" element={<InventorySignOutHistory />} />
                    </Route>
                    <Route path="hr" element={<AdminIndex moduleName="HR" />}>
                      <Route path="" element={<HrModule />} />
                      <Route path="employee-detail/:id" element={<ViewEmployeeDetail />} />
                      <Route path="pto-history/:id" element={<PtoHistory />} />
                      <Route path="create-employee" element={<CreateEmployee />} />
                      <Route path="edit-employee/:id" element={<CreateEmployee />} />
                      <Route path="deleted-employee" element={<DeletedEmployee />} />
                    </Route>

                    <Route path="master-data" element={<AdminIndex moduleName="Master Data" />}>
                      <Route path="assets" element={<Assets />} />
                      <Route path="deleted-assets" element={<DeletedAssets />} />
                      <Route path="categories" element={<Categories />} />
                      <Route path="deleted-categories" element={<DeletedCategories />} />
                      <Route path="services" element={<Services />} />
                      <Route path="deleted-services" element={<DeletedServices />} />
                      <Route path="service/details/:id" element={<ViewServiceDetails />} />
                      <Route path="deleted-tasks" element={<DeletedTasks />} />
                      <Route path="tasks" element={<Tasks />} />
                      <Route path="materials" element={<Materials />} />
                      <Route path="deleted-materials" element={<DeletedMaterials />} />
                      <Route path="consumables" element={<Consumables />} />
                      <Route path="deleted-consumables" element={<DeletedConsumables />} />
                      <Route path="*" element={<PageNotFound />} />
                    </Route>
                    <Route path="admin-settings" element={<AdminIndex moduleName="Admin Settings" />}>
                      <Route path="settings" element={<AdminSettings />} />
                      <Route path="static-data" element={<StaticData />} />
                      <Route path="create-role" element={<AddEditRoleAndPermission />} />
                      <Route path="edit-role/:roleId" element={<AddEditRoleAndPermission />} />
                      <Route path="edit-detail/:id" element={<EditDetailsContactDetails />} />
                      <Route path="email-templates" element={<EmailTemplate />} />
                      <Route path="email-templates/edit/:id" element={<EditEmailTemplate />} />
                      <Route path="static-page" element={<StaticPages />} />
                      <Route path="static-page/edit/:id" element={<EditStaticPage />} />
                    </Route>
                    <Route path="/" element={<Login />} />
                    <Route
                      path="quick-books"
                      element={
                        <AdminIndex moduleName="QuickBooks">
                          <QuickBooks />
                        </AdminIndex>
                      }
                    />
                    <Route
                      path="xero"
                      element={
                        <AdminIndex moduleName="Xero">
                          <Xero />
                        </AdminIndex>
                      }
                    />
                  </Route>
                  <Route path="*" element={<PageNotFound />} />
                </Routes>
              </ErrorBoundary>
            </Suspense>
          </BrowserRouter>
          <ToastHandler />
        </Provider>
      </AppWrapper>
    </ConfigProvider>
  );
}

export default App;
